import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setConsentCookie, getCookie } from '../utils/cookies';
import { generateTCFConsent, storeTCFConsent, isConsentProperlyConfigured } from '../utils/consent';

export interface CookieConsentPreferences {
  ad_Storage: 'granted' | 'denied';
  analytics_Storage: 'granted' | 'denied';
}

interface CookieConsentContextType {
  hasConsent: boolean;
  preferences: CookieConsentPreferences | null;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptCustom: (preferences: CookieConsentPreferences) => void;
  updatePreferences: (preferences: CookieConsentPreferences) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_STORAGE_KEY = 'cookie_consent_preferences';
const CONSENT_VERSION = '2.0'; // Updated version for server-side cookies

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [preferences, setPreferences] = useState<CookieConsentPreferences | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  // Load saved preferences on mount (check server-side cookie first, then localStorage fallback)
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // First try server-side cookie (bypasses ITP)
        const serverCookie = await getCookie('cookie_consent');
        if (serverCookie) {
          try {
            const parsed = JSON.parse(serverCookie);
            if (parsed.version === CONSENT_VERSION && parsed.preferences) {
              setPreferences(parsed.preferences);
              setShowBanner(false);
              // Sync to localStorage as fallback
              localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(parsed));
              return;
            }
          } catch (e) {
            // Invalid JSON, continue to localStorage check
          }
        }

        // Fallback to localStorage
        const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.version === CONSENT_VERSION && parsed.preferences) {
            setPreferences(parsed.preferences);
            setShowBanner(false);
            // Sync to server-side cookie
            await setConsentCookie(parsed);
            return;
          }
        }

        // No valid consent found, show banner
        setShowBanner(true);
      } catch (error) {
        console.error('[Cookie Consent] Failed to load preferences:', error);
        setShowBanner(true);
      }
    };

    loadPreferences();
  }, []);

  const savePreferences = async (newPreferences: CookieConsentPreferences) => {
    try {
      // Validate consent configuration
      if (!isConsentProperlyConfigured(newPreferences)) {
        console.error('[Cookie Consent] Invalid consent configuration');
        return;
      }

      // Generate IAB TCF compliant consent
      const tcfConsent = generateTCFConsent(newPreferences);
      storeTCFConsent(tcfConsent);

      const data = {
        version: CONSENT_VERSION,
        preferences: newPreferences,
        tcfConsent, // Include IAB TCF consent
        timestamp: new Date().toISOString(),
      };
      
      // Save to server-side cookie first (bypasses ITP, 2-year expiration)
      await setConsentCookie(data);
      
      // Also save to localStorage as fallback
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));
      
      setPreferences(newPreferences);
      setShowBanner(false);
    } catch (error) {
      console.error('[Cookie Consent] Failed to save preferences:', error);
      // Fallback: try localStorage only
      try {
        const tcfConsent = generateTCFConsent(newPreferences);
        storeTCFConsent(tcfConsent);
        
        const data = {
          version: CONSENT_VERSION,
          preferences: newPreferences,
          tcfConsent,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));
        setPreferences(newPreferences);
        setShowBanner(false);
      } catch (fallbackError) {
        console.error('[Cookie Consent] Fallback save also failed:', fallbackError);
      }
    }
  };

  const acceptAll = () => {
    const allGranted: CookieConsentPreferences = {
      ad_Storage: 'granted',
      analytics_Storage: 'granted',
    };
    savePreferences(allGranted);
  };

  const rejectAll = () => {
    const allDenied: CookieConsentPreferences = {
      ad_Storage: 'denied',
      analytics_Storage: 'denied',
    };
    savePreferences(allDenied);
  };

  const acceptCustom = (customPreferences: CookieConsentPreferences) => {
    savePreferences(customPreferences);
  };

  const updatePreferences = (newPreferences: CookieConsentPreferences) => {
    savePreferences(newPreferences);
  };

  const hasConsent = preferences !== null;

  return (
    <CookieConsentContext.Provider
      value={{
        hasConsent,
        preferences,
        showBanner,
        acceptAll,
        rejectAll,
        acceptCustom,
        updatePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}

